import type { FullMapModel } from 'src/app/models/form-common/full-map.model';
import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { ParcelasEnvironmentDefinition } from '../environment-variables';
import { ParcelasMapAdapter, type ParcelasMapCatalogContext } from './parcelas-map.adapter';

export type ParcelasMapSubmitAction = 'created' | 'updated' | 'skipped';

export type ParcelasMapSubmitResult = {
    readonly action: ParcelasMapSubmitAction;
    readonly map: FullMapModel | null;
    readonly error?: unknown;
};

export type ParcelasMapSubmitContext = {
    readonly backendResponse: unknown;
    readonly currentMap: FullMapModel | null | undefined;
    readonly env: ParcelasEnvironmentDefinition;
    readonly userId: string;
    readonly catalogContext: ParcelasMapCatalogContext;
    readonly loadExistingMaps: () => Promise<readonly FullMapModel[] | null | undefined>;
    readonly updateMap: (map: FullMapModel) => Promise<unknown>;
    readonly createMap: (map: FullMapModel) => Promise<unknown>;
    readonly logError?: (message: string, error: unknown) => void;
};

/**
 * Synchronizes submitted parcel map data after the parcel backend save has finished.
 */
export class ParcelasMapSubmitAdapter {

    public static async synchronizeSubmittedMap(context: ParcelasMapSubmitContext): Promise<ParcelasMapSubmitResult> {
        const backendParcel = this.resolveBackendParcel(context.backendResponse);

        if (backendParcel === null || !this.hasCoordinates(context.currentMap)) {
            return {
                action: 'skipped',
                map: null,
            };
        }

        const submittedMap = ParcelasMapAdapter.buildSubmittedMapData({
            map: context.currentMap as FullMapModel,
            backendParcel,
            hasClients: context.env.hasClients,
            userId: context.userId,
            catalogContext: context.catalogContext,
        });

        try {
            const existingMaps = await context.loadExistingMaps() ?? [];
            const existingMap = this.findExistingMap(existingMaps, submittedMap.id_parcela);

            if (existingMap?.id) {
                const mapToUpdate = {
                    ...submittedMap,
                    id: existingMap.id,
                } as FullMapModel;

                await context.updateMap(mapToUpdate);

                return {
                    action: 'updated',
                    map: mapToUpdate,
                };
            }

            await context.createMap(submittedMap);

            return {
                action: 'created',
                map: submittedMap,
            };
        } catch (error: unknown) {
            context.logError?.('Error sincronizando mapa_parcelas', error);

            return {
                action: 'skipped',
                map: submittedMap,
                error,
            };
        }
    }

    public static resolveBackendParcel(response: unknown): Partial<ParcelasModel> | null {
        const value = Array.isArray(response) ? response[0] : response;

        return value !== null && typeof value === 'object' ? value as Partial<ParcelasModel> : null;
    }

    private static findExistingMap(
        existingMaps: readonly FullMapModel[],
        parcelId: unknown,
    ): FullMapModel | null {
        const normalizedParcelId = this.toComparableId(parcelId);

        return existingMaps.find((map) => this.toComparableId(map.id_parcela) === normalizedParcelId) ?? null;
    }

    private static hasCoordinates(map: FullMapModel | null | undefined): map is FullMapModel {
        if (map === null || map === undefined) {
            return false;
        }

        const coordinates = map.coordenadas;

        return typeof coordinates === 'string' ? coordinates.trim() !== '' : coordinates !== null && coordinates !== undefined;
    }

    private static toComparableId(value: unknown): string {
        return value === null || value === undefined ? '' : String(value);
    }
}
